import type { TypeTask, TypeTaskState } from '@/types/work/types.work.task';

export const childrenOf = (tasks: TypeTask[], parentId: string | null): TypeTask[] =>
    tasks.filter((task) => task.parentId === parentId).sort((a, b) => a.order - b.order);

export const isLeaf = (tasks: TypeTask[], task: TypeTask): boolean =>
    !tasks.some((entry) => entry.parentId === task.id);

export function descendants(tasks: TypeTask[], taskId: string): TypeTask[] {
    const direct = tasks.filter((task) => task.parentId === taskId);
    return direct.flatMap((child) => [child, ...descendants(tasks, child.id)]);
}

export function ancestors(tasks: TypeTask[], task: TypeTask): TypeTask[] {
    const chain: TypeTask[] = [];
    let current = task.parentId ? tasks.find((entry) => entry.id === task.parentId) : undefined;

    for (let guard = 0; current && guard < 20; guard += 1) {
        chain.unshift(current);
        const parentId: string | null = current.parentId;
        current = parentId ? tasks.find((entry) => entry.id === parentId) : undefined;
    }
    return chain;
}

export function canReparent(tasks: TypeTask[], taskId: string, parentId: string | null): boolean {
    if (!parentId) return true;
    if (taskId === parentId) return false;
    return !descendants(tasks, taskId).some((task) => task.id === parentId);
}

const RANK: Record<TypeTaskState, number> = { idea: 0, todo: 1, doing: 2, review: 3, done: 4 };

export type TypeRollup = {
    leaves: number;
    doneLeaves: number;
    state: TypeTaskState;
    dueAt: string | null;
    hours: number;
    people: string[];
};

export function rollup(tasks: TypeTask[], task: TypeTask): TypeRollup {
    const below = descendants(tasks, task.id);

    if (!below.length) {
        return {
            leaves: 1,
            doneLeaves: task.state === 'done' ? 1 : 0,
            state: task.state,
            dueAt: task.dueAt,
            hours: task.estimateHours ?? 0,
            people: task.assigneeId ? [task.assigneeId] : [],
        };
    }

    const leaves = below.filter((entry) => isLeaf(tasks, entry));
    const doneLeaves = leaves.filter((entry) => entry.state === 'done').length;
    const all = doneLeaves === leaves.length && leaves.length > 0;

    const dates = [task.dueAt, ...below.map((entry) => entry.dueAt)]
        .filter(Boolean)
        .map((value) => Date.parse(value as string))
        .sort((a, b) => a - b);

    const busiest = below
        .filter((entry) => entry.state !== 'done')
        .sort((a, b) => RANK[b.state] - RANK[a.state])[0];

    return {
        leaves: leaves.length,
        doneLeaves,
        state: all ? 'done' : busiest?.state ?? 'idea',
        dueAt: dates.length ? new Date(dates[0]).toISOString() : null,
        hours: below.reduce((sum, entry) => sum + (entry.estimateHours ?? 0), 0) + (task.estimateHours ?? 0),
        people: [...new Set(below.map((entry) => entry.assigneeId).filter(Boolean))] as string[],
    };
}
